import type {
  CuePosture,
  CueType,
  LiveLabAnalysis,
  LiveLabTurn,
  MissedCue,
  Win,
} from '@/types/livelab';

/**
 * Public analyzer contract. Async by design — the production implementation
 * will hit either a CodeBaby meta socket or a hosted LLM. The mock is sync
 * but wrapped in a Promise so callers don't need to change when we swap.
 *
 * Swap site: when an LLM seat is wired, point this at the real implementation
 * (CodeBaby meta socket sending the transcript and parsing structured JSON
 * back, OR a server-side proxy to OpenAI/Anthropic). The shape of
 * LiveLabAnalysis is the contract both implementations must honor.
 */
export async function analyzeTranscript(turns: LiveLabTurn[]): Promise<LiveLabAnalysis> {
  return mockAnalyze(turns);
}

/* ---------------------------- Mock implementation ---------------------------
 *
 * Heuristic, deterministic. Not the product. Looks for customer lines that
 * contain pain/budget/timeline/champion signals and flags the rep response
 * as "missed" if it's short, generic, or pivots to a feature pitch instead
 * of a question/quantification. Wins are rep lines that ask a quantification
 * question, name a specific dollar/time number, or mirror the customer's
 * pain back at them.
 *
 * The intent is plausible-looking analysis on any transcript shape so the UX
 * can be built and demoed before the real LLM seat is connected.
 */

interface SignalMatch {
  cue_type: CueType;
  evidence: string;
}

const PAIN_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\b(spotty|down|outage|outages|crashed?|breaks?|broken|failing)\b/i, label: 'reliability' },
  { re: /\b(slow|lag|laggy|sluggish|hangs?)\b/i, label: 'performance' },
  { re: /\b(frustrat\w*|honestly|painful|nightmare|mess|headache)\b/i, label: 'frustration' },
  {
    re: /\b(can't|cannot|unable to|won't|wouldn't) (do|handle|support|integrate)/i,
    label: 'capability gap',
  },
];

const BUDGET_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\$[\d,]+/, label: 'dollar mention' },
  { re: /\b(budget|cost|spend|paying|price|expensive|cheap)\b/i, label: 'budget word' },
  { re: /\b(quarter|q[1-4]|fiscal|fy\d{2,4})\b/i, label: 'fiscal timing' },
];

const TIMELINE_PATTERNS: { re: RegExp; label: string }[] = [
  {
    re: /\b(by (?:end of |the )?(?:q[1-4]|quarter|month|week|year|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))\b/i,
    label: 'deadline',
  },
  {
    re: /\b(rolling out|launching|going live|kickoff|cutover) (?:in|on|by|next|this)/i,
    label: 'rollout window',
  },
  { re: /\b(asap|urgent|right away|immediately|soon)\b/i, label: 'urgency' },
];

const CHAMPION_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\b(my (?:cfo|cio|cto|boss|director|manager|vp))\b/i, label: 'executive reference' },
  {
    re: /\b(the team|leadership|board|exec) (?:wants|needs|asked|is pushing)/i,
    label: 'stakeholder pressure',
  },
  {
    re: /\b(I('ve| have)|we('ve| have)) (been )?(?:looking|evaluating|considering)/i,
    label: 'active eval',
  },
];

const OBJECTION_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\b(?:we'?re|i'?m) (?:happy|fine|good|all set) with/i, label: 'incumbent satisfaction' },
  { re: /\b(?:not (?:right )?now|not interested|don'?t need|no thanks)\b/i, label: 'flat decline' },
  { re: /\b(too (?:expensive|pricey|much|complicated|complex))\b/i, label: 'cost objection' },
];

const BUYING_QUESTION_PATTERNS: { re: RegExp; label: string }[] = [
  {
    re: /\b(?:how (?:much|long|fast|do|does)|when (?:can|could|would)|what(?:'s| is) the (?:cost|price|next step)|can (?:you|we)|do you have)\b/i,
    label: 'buying question',
  },
];

function detectSignal(text: string): SignalMatch | null {
  for (const p of PAIN_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'pain_hinted', evidence: p.label };
  for (const p of BUDGET_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'budget_signal', evidence: p.label };
  for (const p of TIMELINE_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'timeline_signal', evidence: p.label };
  for (const p of CHAMPION_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'champion_signal', evidence: p.label };
  for (const p of OBJECTION_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'objection', evidence: p.label };
  for (const p of BUYING_QUESTION_PATTERNS)
    if (p.re.test(text)) return { cue_type: 'buying_question', evidence: p.label };
  return null;
}

function isQuestion(text: string): boolean {
  return (
    /\?/.test(text) ||
    /^(what|how|when|where|why|tell me|walk me|help me understand|can you describe)\b/i.test(
      text.trim(),
    )
  );
}

function isQuantifying(text: string): boolean {
  return (
    /\b(how (?:many|much|often|long))\b/i.test(text) ||
    /\b(what does that cost|what'?s the impact|how often does that happen)\b/i.test(text) ||
    /\$[\d,]+/.test(text)
  );
}

function mirrorsPain(repText: string, customerText: string): boolean {
  const customerWords = customerText.toLowerCase().match(/\b[a-z]{5,}\b/g) ?? [];
  if (customerWords.length === 0) return false;
  const distinctive = customerWords.filter((w) =>
    /(spott|outag|down|crash|frustrat|expens|slow|broken|fail|nightmare|painful|mess)/.test(w),
  );
  if (distinctive.length === 0) return false;
  const lower = repText.toLowerCase();
  return distinctive.some((w) => lower.includes(w));
}

function isShort(text: string): boolean {
  return text.trim().split(/\s+/).length < 8;
}

function isFeaturePitch(text: string): boolean {
  return /\b(our (?:platform|solution|product|service)|we offer|we provide|we have a|comcast (?:business )?(?:offers|provides))/i.test(
    text,
  );
}

function whyMissedCopy(cue: CueType, repText: string): string {
  const responseShape = isFeaturePitch(repText)
    ? 'pivoted to a feature pitch'
    : isShort(repText)
      ? 'gave a short response and moved on'
      : 'kept the surface-level read instead of going deeper';
  switch (cue) {
    case 'pain_hinted':
      return `The customer named a real pain point and you ${responseShape} — that's where the next question lives.`;
    case 'budget_signal':
      return `Budget was on the table and you ${responseShape} instead of mapping the spend to outcomes.`;
    case 'timeline_signal':
      return `The customer told you when this matters and you ${responseShape} — anchoring to that timeline shifts urgency.`;
    case 'champion_signal':
      return `The customer flagged a stakeholder and you ${responseShape} — that's a champion thread to pull.`;
    case 'objection':
      return `An objection came up and you ${responseShape} — reframe rather than retreat.`;
    case 'buying_question':
      return `The customer asked a buying question and you ${responseShape} — that's a green light to take the next step.`;
  }
}

function whatToTryCopy(cue: CueType): string {
  switch (cue) {
    case 'pain_hinted':
      return 'Try: "When that happens, what does it cost you?" or "How often is that the case?"';
    case 'budget_signal':
      return 'Try: tie your number to the cost they just named — "for less than the cost of those outages, here\'s what\'s included."';
    case 'timeline_signal':
      return 'Try: "If we\'re live by [their date], what\'s the first thing you want to be true?"';
    case 'champion_signal':
      return 'Try: "What does [stakeholder] need to see to feel good about this?" — earn the multi-thread.';
    case 'objection':
      return 'Try: agree-then-reframe. "I get that — and the reps who say that usually find that…"';
    case 'buying_question':
      return 'Try: answer concisely, then advance — "Yes — what would make sense as a next step?"';
  }
}

function whyStrongCopy(repText: string): string {
  if (isQuantifying(repText))
    return "You quantified the pain. That's how it becomes a reason to act.";
  if (isQuestion(repText))
    return "You asked instead of pitched. That's the move that earns the next layer.";
  return "You mirrored their language back. That's how customers feel heard, not sold to.";
}

function callPosture(turns: LiveLabTurn[]): { posture: CuePosture; evidence: string } {
  const customer = turns.filter((t) => t.speaker === 'customer');
  const earlyHostile = customer
    .slice(0, 3)
    .some((t) =>
      /\b(?:i don'?t (?:have|need)|not interested|waste of time|already told)\b/i.test(t.text),
    );
  if (earlyHostile) {
    const ev =
      customer.find((t) => /\b(?:i don'?t|not interested|waste|already told)\b/i.test(t.text))
        ?.text ?? '';
    return { posture: 'hostile', evidence: ev };
  }
  const defensive = customer.some((t) =>
    /\b(?:we'?re happy with|all set|don'?t need|fine where we are)\b/i.test(t.text),
  );
  if (defensive) {
    const ev =
      customer.find((t) => /\b(?:happy with|all set|don'?t need|fine where we are)\b/i.test(t.text))
        ?.text ?? '';
    return { posture: 'defensive', evidence: ev };
  }
  const skeptical = customer.some((t) =>
    /\b(?:what would make this different|prove it|why should we|skeptical|not convinced)\b/i.test(
      t.text,
    ),
  );
  if (skeptical) {
    const ev =
      customer.find((t) =>
        /\b(?:make this different|prove it|why should|skeptical|not convinced)\b/i.test(t.text),
      )?.text ?? '';
    return { posture: 'skeptical', evidence: ev };
  }
  const warm = customer.some((t) =>
    /\b(?:tell me more|interested|sounds (?:good|interesting)|we'?d love)\b/i.test(t.text),
  );
  if (warm) {
    const ev =
      customer.find((t) => /\b(?:tell me more|interested|sounds|love)\b/i.test(t.text))?.text ?? '';
    return { posture: 'warm', evidence: ev };
  }
  return {
    posture: 'skeptical',
    evidence:
      'Default read in absence of strong signals — assume the customer is evaluating, not buying yet.',
  };
}

function summarize(
  turns: LiveLabTurn[],
  cues: MissedCue[],
  wins: Win[],
  posture: CuePosture,
): string {
  const repTurns = turns.filter((t) => t.speaker === 'rep').length;
  if (cues.length === 0 && wins.length === 0) {
    return `${repTurns} rep turns, ${turns.length - repTurns} customer turns. The transcript didn't surface obvious cues to flag — try a longer or more substantive call.`;
  }
  const postureLine =
    posture === 'warm'
      ? 'The customer was leaning in — the question is what you did with the room you were handed.'
      : posture === 'defensive'
        ? 'The customer was guarded and incumbent-satisfied — every cue had to be earned.'
        : posture === 'hostile'
          ? 'The customer was hostile from the open — flagging missed cues here is about the few moments any opening existed at all.'
          : 'The customer was evaluating, not buying — they gave you signals; the work was reading them.';
  const winLine =
    wins.length > 0
      ? `${wins.length} moment${wins.length === 1 ? '' : 's'} where you played the move.`
      : 'No moments stood out as wins on this read.';
  const missLine =
    cues.length > 0
      ? `${cues.length} cue${cues.length === 1 ? '' : 's'} on the table that the response went vague on.`
      : 'No missed cues this pass.';
  return `${postureLine} ${missLine} ${winLine}`;
}

function mockAnalyze(turns: LiveLabTurn[]): LiveLabAnalysis {
  const missed_cues: MissedCue[] = [];
  const wins: Win[] = [];

  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i];
    if (!turn || turn.speaker !== 'customer') continue;
    const signal = detectSignal(turn.text);
    if (!signal) continue;
    const next = turns[i + 1];
    if (!next || next.speaker !== 'rep') continue;

    const repText = next.text;
    const responded_well =
      isQuantifying(repText) ||
      (isQuestion(repText) && !isFeaturePitch(repText)) ||
      mirrorsPain(repText, turn.text);

    if (responded_well) {
      wins.push({
        rep_quote: repText,
        why_strong: whyStrongCopy(repText),
        at_turn_index: i + 1,
      });
    } else {
      missed_cues.push({
        cue_type: signal.cue_type,
        customer_quote: turn.text,
        rep_response: repText,
        why_missed: whyMissedCopy(signal.cue_type, repText),
        what_to_try: whatToTryCopy(signal.cue_type),
        at_turn_index: i,
      });
    }
  }

  const { posture, evidence } = callPosture(turns);

  return {
    generated_at: new Date().toISOString(),
    source: 'mock',
    summary: summarize(turns, missed_cues, wins, posture),
    posture,
    posture_evidence: evidence,
    missed_cues,
    wins,
  };
}
